import { Menu, Table } from '@mantine/core';
import { IconEdit, IconTrash, IconUser } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DeleteModal } from '../../../components/DeleteModal';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { useModal } from '../../../hooks/useModal';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import {
	formatSecondsToHours,
	formatTillId,
	formatToDateString,
	formatToTimeString,
} from '../../../util/formatting';
import type { AttendanceDto } from '../types/AttendanceDto';
import { EditAttendanceModal } from './EditAttendanceModal';
import { KeyedRow } from '../../../components/KeyedRow';

interface IndividualAttendanceRowProps {
	initialAttendance: AttendanceDto;
	refreshTables: () => void;
}

export function IndividualAttendanceRow({
	initialAttendance,
	refreshTables,
}: IndividualAttendanceRowProps) {
	const deleteModal = useModal();
	const editModal = useModal();

	const navigate = useNavigate();

	const { organisation } = useParams();
	const [attendance, setAttendance] = useState(initialAttendance);

	const deleteMutation = mutate<boolean>(
		`${organisation}/attendance/delete/${attendance.id.toString()}`,
	);

	return (
		<>
			<DeleteModal
				close={deleteModal.close}
				isOpened={deleteModal.isOpen}
				titleType={'Attendance Record'}
				itemDescription={attendance.operatorName}
				mutation={deleteMutation}
				onComplete={refreshTables}
			/>
			<EditAttendanceModal
				attendance={{
					branchId: attendance.branchId.toString(),
					tillNo: formatTillId(
						attendance.tillName,
						attendance.tillNo.toString(),
						attendance.branchId.toString(),
					),
					operatorId: attendance.operatorId.toString(),
					startTime: new Date(attendance.startTime),
					endTime: new Date(attendance.endTime),
					id: attendance.id,
				}}
				close={editModal.close}
				isOpened={editModal.isOpen}
				updateTableValue={setAttendance}
				refreshGroupTable={refreshTables}
			/>
			<KeyedRow>
				<Table.Td>{attendance.branchName}</Table.Td>
				<Table.Td>{attendance.operatorName}</Table.Td>
				<Table.Td>{attendance.tillName}</Table.Td>
				<Table.Td>{formatToDateString(attendance.startTime)}</Table.Td>
				<Table.Td>{formatToTimeString(attendance.startTime)}</Table.Td>
				<Table.Td>{formatToDateString(attendance.endTime)}</Table.Td>
				<Table.Td>{formatToTimeString(attendance.endTime)}</Table.Td>
				<Table.Td>{formatSecondsToHours(attendance.totalSeconds)}</Table.Td>
				<Table.Td>
					<TableRowMenu>
						<Menu.Item
							leftSection={<IconUser {...defaultIconStyle} />}
							onClick={() =>
								navigate(
									`/${organisation}/staff/member/${attendance.operatorId}`,
								)
							}
							data-cy={'staff-button'}
						>
							Staff Member
						</Menu.Item>
						<Menu.Item
							leftSection={<IconEdit {...defaultIconStyle} />}
							onClick={() => editModal.open()}
							data-cy={'edit-button'}
						>
							Edit
						</Menu.Item>
						<Menu.Item
							leftSection={<IconTrash {...defaultIconStyle} />}
							c={'red'}
							onClick={() => deleteModal.open()}
							data-cy={'delete-button'}
						>
							Delete
						</Menu.Item>
					</TableRowMenu>
				</Table.Td>
			</KeyedRow>
		</>
	);
}
